import {
  ApiResponse,
  FormStore,
  GlobalStore,
  UserService,
} from '@roc/feature-app-core';
import {
  action,
  computed,
  flow,
  makeObservable,
  observable,
  override,
} from 'mobx';
import { SecondAppraisalService } from '../services/secondAppraisalService';

import { GENERIC_ERROR_MESSAGE, isNil, LoanSubType } from '@roc/feature-utils';

const form = {
  fields: {
    paymentBy: {
      value: null,
      error: null,
      rule: 'required',
    },
    appraisalType: {
      value: null,
      error: null,
      rule: 'required',
    },
  },
  meta: {
    isValid: false,
    error: null,
  },
};

export enum SecondAppraisalStep {
  SELECT_PROPERTY,
  ALREADY_PAID,
  ERROR,
  SUCCESS,
  SUCCESS_ALREADY_PAID,
  ORDER_CREATED,
}

export class InternalSecondAppraisalStore {
  globalStore: GlobalStore;
  loanDetails: any = undefined;
  document: any = undefined;
  loanProperties: any = [];
  allLoanProperties: any = [];
  loanPropertiesSelectedNonPaid: any = [];
  alreadyPaidProperties: any = [];
  selectedPropertyIds: string[];
  loanSubtype: any;
  showCDATab: any;
  secondAppraisalService: SecondAppraisalService;
  secondAppraisalForm: FormStore;

  step: SecondAppraisalStep;

  formLink: string;

  constructor(globalStore: GlobalStore) {
    this.secondAppraisalService = new SecondAppraisalService();
    this.globalStore = globalStore;
    this.selectedPropertyIds = [];
    this.secondAppraisalForm = new FormStore(form, globalStore);
    makeObservable(this, {
      loanDetails: observable,
      document: observable,
      loanProperties: observable,
      loanPropertiesSelectedNonPaid: observable,
      selectedPropertyIds: observable,
      loanSubtype: observable,
      showCDATab: observable,
      step: observable,
      setPropertyIds: action,
      removePropertyIds: action,
      fetchLoanPropertiesAndCheckStatus: flow,
      orderSecondAppraisal: flow,
      copyLink: action,
      sendLink: flow,
      allowOrderSecondAppraisal: computed,
      allLoanProperties: observable,
    });
  }

  setPropertyIds(value, selected) {
    console.log(value);
    if (value != null && !selected) {
      this.removePropertyIds(value);
    } else if (value != null && !this.selectedPropertyIds.includes(value)) {
      this.selectedPropertyIds.push(value);
    }
  }

  removePropertyIds(value) {
    this.selectedPropertyIds = this.selectedPropertyIds.filter(
      propertyId => propertyId != value
    );
  }
  *fetchLoanPropertiesAndCheckStatus(loanId: string) {
    try {

      const secondAppraisalResponse = yield this.secondAppraisalService.getSecondAppraisalInformation(
        loanId
      );
      this.loanSubtype = secondAppraisalResponse?.data?.loanSubtype;
      this.showCDATab = secondAppraisalResponse?.data?.showCDATab;
      this.loanProperties = secondAppraisalResponse?.data?.nonSelectedProperties;
      this.loanPropertiesSelectedNonPaid = secondAppraisalResponse?.data?.loanPropertiesSelectedNotPaid;
      const alreadPaidResponse = yield this.secondAppraisalService.getPropertiesWithSecondAppraisalPaymentDone(
        loanId
      );
      const propertiesResponse = yield this.secondAppraisalService.getLoanProperties(
        loanId
      );
      this.allLoanProperties = propertiesResponse.data.data;
      this.alreadyPaidProperties = alreadPaidResponse.data || [];
      const existingLinkResponse = yield this.secondAppraisalService.getSecondAppraisalLinkIfPropertiesAlreadySelectedForPayment(
        loanId
      );
      const paymentLink = secondAppraisalResponse?.data?.paymentLink;
      const selectedPropertyIds = existingLinkResponse?.data?.selectedPropertyIds;

      if (!secondAppraisalResponse?.data?.allFirstAppraisalIn) {
        this.step = SecondAppraisalStep.ERROR;
      }else if(this.loanSubtype === LoanSubType.RENTAL_PORTFOLIO && secondAppraisalResponse?.data?.missingSelectedAppraisals){
        this.step = SecondAppraisalStep.SUCCESS;
        this.formLink = paymentLink;
      }else if (this.loanSubtype === LoanSubType.RENTAL_PORTFOLIO && secondAppraisalResponse?.data?.missingNonSelectedAppraisals){
        this.step = SecondAppraisalStep.SELECT_PROPERTY;
      }
      else if (secondAppraisalResponse?.data?.ordersCreated) {
        this.step = SecondAppraisalStep.ORDER_CREATED;
        this.selectedPropertyIds = this.alreadyPaidProperties;
      } else if (secondAppraisalResponse?.data?.paid) {
        this.step = SecondAppraisalStep.ALREADY_PAID;
        this.selectedPropertyIds = this.alreadyPaidProperties;
      }else {
        if (paymentLink && secondAppraisalResponse?.data?.allFirstAppraisalIn) {
          this.formLink = paymentLink;
          this.selectedPropertyIds = selectedPropertyIds;
          this.step = SecondAppraisalStep.SUCCESS;
        }
        else if (isNil(paymentLink) && secondAppraisalResponse?.data?.allFirstAppraisalIn) {
          this.step = SecondAppraisalStep.SELECT_PROPERTY;
          this.selectedPropertyIds = this.loanProperties.map(
            property => property.propertyId
          );
        }
        else if (('Rental Portfolios' === this.loanSubtype) && !secondAppraisalResponse?.data?.allFirstAppraisalIn) {
          this.step = SecondAppraisalStep.SELECT_PROPERTY;
          this.selectedPropertyIds = this.loanProperties.map(
            property => property.propertyId
          );
        }
        else {
          this.step = SecondAppraisalStep.ERROR;
          this.selectedPropertyIds = this.loanProperties.map(
            property => property.propertyId
          );
        }
      }
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  *orderSecondAppraisal(loanId: string, paymentDone: boolean) {
    try {
      const response: ApiResponse = yield this.secondAppraisalService.orderSecondAppraisal(
        loanId,
        this.selectedPropertyIds,
        paymentDone
      );
      const data = response.data;
      if (data?.cardknoxUrl) {
        this.formLink = data.cardknoxUrl;
        this.step = SecondAppraisalStep.SUCCESS;
      } else {
        this.step = SecondAppraisalStep.SUCCESS_ALREADY_PAID;
      }
    } catch (error) {
      this.step = SecondAppraisalStep.ERROR;
      console.log(error);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while Ordering Second Appraisal',
      });
    }
  }

  copyLink() {
    navigator.clipboard.writeText(this.formLink);
    this.globalStore.notificationStore.showSuccessNotification({
      message: 'The link was copied to clipboard!',
    });
  }

  *sendLink(loanId: string) {
    try {
      yield this.secondAppraisalService.sendLink(loanId);
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'The link was sent to the underwriting chatroom!',
      });
    } catch (e) {
      this.globalStore.notificationStore.showSuccessNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  get allowOrderSecondAppraisal(){
    return this.globalStore.userFeatures?.allowOrderSecondAppraisal;
  }
}
